import React, { useState } from 'react'
import { addDoc, collection } from 'firebase/firestore'
import { db } from '../../firebase.config'
import { useAuth } from '../../context/AuthContext'

const CATEGORIES = ['Engineering', 'Execution', 'Project Quality', 'Safety']
const WORK_AREAS = ['Substation', 'BESS', 'Collection', 'TLine']
const SEVERITIES = ['Low', 'Medium', 'High', 'Critical']
const PROJECTS = ['Project Alpha', 'Project Beta', 'Project Gamma']

interface Props {
  onClose: () => void
  onCreated: () => void
}

export default function NearMissModal({ onClose, onCreated }: Props) {
  const { orgId, userData } = useAuth()
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [category, setCategory] = useState(CATEGORIES[0])
  const [workArea, setWorkArea] = useState(WORK_AREAS[0])
  const [severity, setSeverity] = useState('Low')
  const [project, setProject] = useState(PROJECTS[0])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!orgId) {
      setError('orgId is missing — please log out and back in')
      return
    }
    setSaving(true)
    setError('')
    try {
      await addDoc(collection(db, 'organizations', orgId, 'nearMiss'), {
        orgId,
        title,
        description,
        category,
        workArea,
        severity,
        project,
        status: 'Open',
        createdBy: userData?.name || userData?.email || 'unknown',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      })
      onCreated()
      onClose()
    } catch (err: any) {
      setError(err?.message || 'Failed to create entry. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg mx-4">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">New Near Miss / Good Catch</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">×</button>
        </div>

        {error && (
          <div className="mx-6 mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-lg text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
            <input
              type="text"
              value={title}
              onChange={e => setTitle(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm"
              placeholder="Brief description of the near miss or good catch"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm"
              rows={3}
              placeholder="What happened or what was observed"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <select value={category} onChange={e => setCategory(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm bg-white">
                {CATEGORIES.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Work Area</label>
              <select value={workArea} onChange={e => setWorkArea(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm bg-white">
                {WORK_AREAS.map(w => <option key={w}>{w}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Severity</label>
              <select value={severity} onChange={e => setSeverity(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm bg-white">
                {SEVERITIES.map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Project</label>
              <select value={project} onChange={e => setProject(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm bg-white">
                {PROJECTS.map(p => <option key={p}>{p}</option>)}
              </select>
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 border border-gray-300 text-gray-700 py-2.5 rounded-lg font-medium hover:bg-gray-50">
              Cancel
            </button>
            <button type="submit" disabled={saving}
              className="flex-1 bg-accent text-white py-2.5 rounded-lg font-medium hover:bg-amber-700 disabled:bg-amber-300">
              {saving ? 'Saving...' : 'Create Entry'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
